import createModule from "../createModule"
import view from "./view"
import { get, size } from "lodash"
import { generateClassificationQuery, classificationsQuery } from "helpers/classification"
import { Creators } from "./model"
import { Creators as filterSaverCreators } from "modules/filter-saver/model"
import { getUnmodeledDates } from "selectors/dates"
import { getHistoricalFactorSensitivityIsAvailable, getSort, getPage, getSelectedColumns } from "./selectors"
import { getModelStartDate, getModelCurrentDate, getSelectedDate, getSelectedModelId, getIsSelectedDateAdjusted } from "./selectors/dates"
import { getPortfoliosList, getWatchlistsList, getEtfsList, getSwapsList, getIndexesList } from "./selectors/universes"
import { getValidFilter, getFilterId, getFacetedFilter, getFacetedFilterId, getClassificationsTiers, getCurrency, getCountryFacets, getCurrencyFacets, getClassificationFacets } from "./selectors/filter"
import { BASKETS_QUERY } from "resources/baskets"
import withOutletContext from "hocs/withOutletContext"
import { MODEL_METADATA_QUERY } from "resources/models"
import { CONTENT_SETS_METADATA_QUERY } from "resources/content-sets"
import "./styles.scss"

export default withOutletContext(createModule({
  view,
  externalProps: (state, props) => ({
    account: state.global.selected.account,
    accountId: state.global.selected.accountId,
    modelId: getSelectedModelId(state, props),
    startDate: getModelStartDate(state, props),
    endDate: getModelCurrentDate(state, props),
    selectedDate: getSelectedDate(state, props),
    classificationsTiers: getClassificationsTiers(state, props),
  }),
  required: ["account", "accountId", "modelId", "selectedDate"],
  data: [{
    resource: "modelCountries",
    url: ({ account, modelId }) => `/api/${account}/m/${modelId}/countries`,
  }, {
    resource: "modelCurrencies",
    url: ({ account, modelId }) => `/api/${account}/m/${modelId}/currencies`,
  }, {
    resource: "modeledDates",
    url: ({ modelId, startDate, endDate }) => {
      if (!startDate || !endDate) {
        return null
      }

      return {
        id: `${modelId}${startDate}${endDate}`,
        query: "query ($modelId: String!, $from: Date!, $to: Date!) { model (id: $modelId) { availability { dates(from: $from, to: $to) } } }",
        variables: { modelId, from: startDate, to: endDate },
      }
    },
  }, {
    resource: "etfs",
    url: ({ account, modelId }) => `/api/${account}/m/${modelId}/etfs`,
  }, {
    resource: "watchlists",
    url: () => {
      return {
        id: "watchlists",
        query: `{
          watchlists {
            id
            alias
            name
          }
        }`,
      }
    },
  }, {
    resource: "indices",
    as: null,
    url: () => ({
      id: "indices",
      query: `query {
        indexes { id name description availableFrom }
      }`,
    }),
  }, BASKETS_QUERY, CONTENT_SETS_METADATA_QUERY, {
    resource: "classifications",
    url: () => ({
      id: "classifications",
      query: classificationsQuery,
    }),
  }, {
    resource: "classificationsValues",
    url: ({ classificationsTiers, selectedDate }) => {
      if (!classificationsTiers || size(classificationsTiers) === 0 || !selectedDate) {
        return null
      }

      // We don't know the depth of each classification, generate each classification sub query according to the tier depth
      const innerQuery = generateClassificationQuery(classificationsTiers)
      return {
        id: `classificationsValues-${selectedDate}-${JSON.stringify(classificationsTiers)}`,
        query: `query {
            ${innerQuery}
          }`,
      }
    },
  }, {
    resource: "historicalFactorSensitivityAvailability",
    url: ({ account, modelId, selectedDate }) => ({
      url: `/api/${account}/m/${modelId}/factorReturns/hasEnoughHistory`,
      id: `historicalFactorSensitivityAvailability-${selectedDate}-${modelId}-${account}`,
      payload: { selectedDate },
    }),
  }, {
    resource: "securityFinderPreferences",
    url: ({ accountId, modelId }) => ({
      id: `securityFinderPreferences-${accountId}${modelId}`,
      url: "/api/users/me/preferences",
      payload: {
        items: [
          {
            type: "securityFinderSelectedColumns",
            key: modelId,
            accountId,
          },
        ],
      },
    }),
  },
  MODEL_METADATA_QUERY,
  ],
  actions: {
    ...Creators,
    resetFilterSaver: filterSaverCreators.setCurrentFilter,
  },
  mapStateToProps: (state, props) => ({
    portfoliosList: getPortfoliosList(state),
    watchlistsList: getWatchlistsList(state),
    etfsList: getEtfsList(state, props),
    swapsList: getSwapsList(state, props),
    indexesList: getIndexesList(state, props),
    unmodeledDates: getUnmodeledDates(state, props),
    name: get(state, "modules.filterSaver.currentFilterName"),
    filter: getValidFilter(state, props),
    filterId: getFilterId(state, props),
    facetedFilter: getFacetedFilter(state, props),
    facetedFilterId: getFacetedFilterId(state, props),
    currency: getCurrency(state, props),
    isHistoricalFactorSensitivityAvailable: getHistoricalFactorSensitivityIsAvailable(state, props),
    sort: getSort(state, props),
    page: getPage(state, props),
    selectedCountries: getCountryFacets(state, props),
    selectedCurrencies: getCurrencyFacets(state, props),
    selectedClassifications: getClassificationFacets(state, props),
    selectedColumns: getSelectedColumns(state, props),
    selectedDateAdjusted: getIsSelectedDateAdjusted(state, props),
    allowFiltersToggle: true,
  }),
}))
